/**
 * MicrophoneButton.js
 * Reusable microphone button component for speech-to-text in Constellation KG
 * 
 * This component provides a consistent UI for voice input across the app:
 * - AI Explorer (query input)
 * - My Journal (thoughts input)
 * 
 * Features:
 * - Animated recording indicator (pulsing border)
 * - Accessible (aria labels, keyboard support)
 * - Automatic icon switching between idle and recording states
 * - Optional status message display
 * - Disabled state when speech recognition is not enabled/supported
 * 
 * Usage:
 * 
 *   import MicrophoneButton from '../../common/MicrophoneButton/MicrophoneButton';
 *   
 *   const MyComponent = () => {
 *     const textareaRef = useRef(null);
 *     
 *     return (
 *       <>
 *         <textarea ref={textareaRef} />
 *         <MicrophoneButton 
 *           textareaRef={textareaRef}
 *           maxLength={3000}
 *           onTranscriptChange={(text) => setFormData(prev => ({ ...prev, content: text }))}
 *         />
 *       </>
 *     );
 *   };
 */

import React, { useCallback } from 'react';
import {
  useSpeechRecognitionContext,
  ON_DEVICE_STATES,
  DICTATION_REFUSED_MESSAGE
} from '../../../contexts/SpeechRecognitionContext';
import './MicrophoneButton.css';

// =========================================
// Icon Components
// =========================================

/**
 * Microphone icon for idle state
 */
const MicrophoneIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="20" 
    height="20" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2"
    strokeLinecap="round" 
    strokeLinejoin="round" 
    aria-hidden="true"
  >
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="22" />
  </svg>
);

/**
 * Microphone icon for recording state (filled)
 */
const MicrophoneActiveIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="20" 
    height="20" 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    stroke="currentColor" 
    strokeWidth="2"
    strokeLinecap="round" 
    strokeLinejoin="round" 
    aria-hidden="true"
  >
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="22" />
  </svg>
);

// =========================================
// Main Component
// =========================================

/**
 * MicrophoneButton
 * 
 * A button that toggles speech-to-text recording for a target textarea.
 * 
 * @param {Object} props
 * @param {React.RefObject<HTMLTextAreaElement>} props.textareaRef - Ref to the target textarea
 * @param {number|null} props.maxLength - Maximum character length (null for no limit)
 * @param {function|null} props.onTranscriptChange - Callback when transcript changes (for React state sync)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showStatus - Whether to show status message below button (default: false)
 * @param {boolean} props.disabled - Force disabled state
 */
const MicrophoneButton = ({ 
  textareaRef,
  maxLength = null,
  onTranscriptChange = null,
  className = '',
  showStatus = false,
  disabled = false
}) => {
  // Get speech recognition state from context.
  //
  // `statusMessage` and `statusType` are deliberately NOT read here any more:
  // this component stopped rendering a status line on October 13, 2026 and the
  // callers render it full width below their text fields instead. The
  // STATUS_TYPES-from-context bug that lived here (it is a module export, never
  // part of the context value, so the error class never applied) went with the
  // code that depended on it.
  const {
    isRecording,
    isEnabled,
    isSupported,
    isDictationRefused,
    onDeviceState,
    toggleRecording
  } = useSpeechRecognitionContext();

  // =========================================
  // Event Handlers
  // =========================================

  /**
   * Handle button click - toggle recording
   */
  const handleClick = useCallback(() => {
    toggleRecording(textareaRef, maxLength, onTranscriptChange);
  }, [toggleRecording, textareaRef, maxLength, onTranscriptChange]);

  /**
   * Handle keyboard interaction for accessibility
   */
  const handleKeyDown = useCallback((event) => {
    // Activate on Enter or Space
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  // =========================================
  // Render Logic
  // =========================================

  // Don't render if speech recognition is disabled via the sidebar toggle.
  // That is the user's own choice, not a refusal, and there is nothing to
  // explain to them.
  if (!isEnabled) {
    return null;
  }

  // DICTATION REFUSED — the control stays VISIBLE and DISABLED, and carries
  // the message (ruling point 6, October 12, 2026). It used to return null when
  // `isSupported` was false, which told the user nothing: the button simply was
  // not there. Both conditions land here: no Web Speech API at all, and an API
  // that cannot be made to recognize on the device.
  const refused = !isSupported || isDictationRefused;

  // Mid-probe or mid-download: not clickable yet, but not a refusal either.
  const busyWithSetup =
    onDeviceState === ON_DEVICE_STATES.CHECKING ||
    onDeviceState === ON_DEVICE_STATES.PREPARING;

  // Determine button state
  const isDisabled = disabled || refused || busyWithSetup;
  const isActive = isRecording;

  // Build CSS classes
  const buttonClasses = [
    'mic-button',
    isActive ? 'recording' : '',
    isDisabled ? 'disabled' : '',
    refused ? 'unavailable' : '',
    className
  ].filter(Boolean).join(' ');

  // Determine aria label based on state. The refusal is put on the control
  // itself so a screen reader reaches it without depending on showStatus.
  const ariaLabel = refused
    ? DICTATION_REFUSED_MESSAGE
    : isActive
      ? 'Stop voice input'
      : 'Start voice input';

  // Determine title (tooltip) based on state
  const title = refused
    ? DICTATION_REFUSED_MESSAGE
    : busyWithSetup
      ? 'Preparing on-device dictation...'
      : isActive
        ? 'Click to stop dictation'
        : 'Click to dictate';

  return (
    <div className="mic-button-container">
      <button
        type="button"
        className={buttonClasses}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        aria-label={ariaLabel}
        aria-pressed={isActive}
        title={title}
      >
        {isActive ? <MicrophoneActiveIcon /> : <MicrophoneIcon />}
      </button>
      
      {/* NO STATUS LINE HERE, as of October 13, 2026.
          It used to render inside this container, which is an inline column
          beside the text field, and `.mic-status` caps at 150px — so every
          dictation message wrapped into a narrow strip and pushed the field
          down (observed by Dennis). The messages now go in the full-width
          `.stt-status-message` line below the text field, where "Listening..."
          already appeared: Journal.js and AIExplorerPage.js render it from the
          same `statusMessage`. `showStatus` is kept in the prop list for
          callers that still pass it, and is deliberately inert. */}
    </div>
  );
};

export default MicrophoneButton;
